// =====================================================================
// src/screens/v2/BusinessProfile.jsx
//
// V2 Business Profile screen.
//
// As of Step 3.3 (V2 Phase B), data is read from Firestore via
// getBusiness() and getProducts(). Connection state is still
// localStorage-backed; Step 3.4 will wire it to /connections in Firestore.
//
// Props:
//   businessId   — which business to show (slug like 'sharma-sweets')
//   onBack       — go back to previous V2 screen (Directory)
//   onExitV2     — exit V2 entirely, return to V1
// =====================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Share2, MoreVertical, MapPin, Check,
  Clock, Truck, FileText, Phone, MessageCircle, ShoppingCart, Plus,
  Settings,
} from 'lucide-react';
import {
  getBusiness,
  getProducts,
  logoBgFromCategory,
  categoryLabelFromId,
  todayHoursStr,
  formatHoursForDisplay,
  getConnection,
  connectToBusiness,
  disconnectFromBusiness,
  // Item #13 Phase A Step 6 Part B — "Notify when open" subscription
  isSubscribedToOpenAlert,
  subscribeToOpenAlert,
  unsubscribeFromOpenAlert,
} from '../../lib/v2/firestore';
// Centralised override-aware open-status helper. Wraps the hours-only
// openStatus() with the merchant's manualOpenOverride field. Use this
// instead of computing the override inline (which used to have a bug
// where a force-open business pre-opening-hours would display its
// contradictory hours-based "Opens at HH AM" string).
import { getEffectiveOpenStatus } from '../../lib/v2/openStatusOverride';
import { isBusinessActive } from '../../lib/v2/isBusinessActive';
import { auth } from '../../lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { addToCart, getCartCount } from '../../lib/v2/cart';
// Phase 2.5 / C10c — wall posts on biz profile
import { listWallPosts, toggleSpark, getSparkedPostIds } from '../../lib/v2/wallPosts';
import WallPostCard from './WallPostCard';
import { submitBusinessReport } from '../../lib/db';
import SharePostSheet from './SharePostSheet';
import ImageViewerModal from '../../components/ImageViewerModal';
import './BusinessProfile.css';
import { buildShareUrl } from '../../lib/shareBase';

// Item #8 — Categories that require FSSAI under Indian food-safety
// law. Mirrors the `food: true` flag in RegisterBusiness's CATEGORIES
// array. Used to gate the customer-side FSSAI display section so
// non-food businesses don't get an irrelevant "License" panel.
const FOOD_CATEGORIES = new Set(['food_beverages', 'dairy', 'bakery', 'handmade']);

// ============================================================
// Profile-specific transforms (Step 3.3)
// ============================================================
//
// transformBusinessProfile maps a raw Firestore business doc + its
// products list into the rich shape this screen's JSX expects.
// Posts are not yet stored in Firestore; left empty for now.
//
function transformBusinessProfile(doc, productsRaw) {
  // Override-aware open status (Item #13 follow-up). Replaces the
  // inline manualOpenOverride application we previously duplicated
  // here; centralised in openStatusOverride.js. Returns
  // { open, openMeta } where openMeta is "Open till 9 PM" /
  // "Opens at 10 AM" / "Closed today" / "Open today" etc.
  const status = getEffectiveOpenStatus(doc);
  const bg = logoBgFromCategory(doc.category);

  // Item #12 — Distinguish "manually paused via merchant toggle"
  // (override.open === false for today) from "outside hours". Drives
  // different toast wording in handleCall / handleWhatsApp. Different
  // concept than open/openMeta, so keep it computed locally.
  const todayStr = new Date().toISOString().split('T')[0];
  const ovr = doc.manualOpenOverride;
  const manuallyPaused = !!(ovr && ovr.date === todayStr && ovr.open === false);

  return {
    // Identity + ownership (Y1: needed to detect viewer-is-owner)
    ownerId: doc.ownerId,
    status: doc.status,                  // 'verified' | 'pending_verification' | 'suspended'
    trialStartedAt: doc.trialStartedAt,  // for future trial-expired UI
    trialDays: doc.trialDays,
    name: doc.name,
    tagline: doc.shortDescription || '',
    categoryLabel: categoryLabelFromId(doc.category),
    verified: !!doc.verified,
    open: status.open,
    openTimeStr: status.openMeta,
    manuallyPaused,
    todayHoursStr: todayHoursStr(doc.hours),
    locality: doc.locality || '',
    city: doc.city || '',
    logo: doc.logo || '🏪',
    logoBg: bg,
    coverBg: bg,
    // Phase 2.5 / C10c — Customer-side was rendering the category emoji
    // even when the merchant had uploaded a real logo + cover photo.
    // Mirror the same fields the merchant top bar uses (logoImageUrl,
    // coverUrl) — render branches on truthiness in the hero block.
    logoImageUrl: doc.logoImageUrl || null,
    coverUrl: doc.coverUrl || null,
    description: doc.description || '',
    address: [doc.locality, doc.city].filter(Boolean).join(', ') || '—',
    hours: formatHoursForDisplay(doc.hours),
    delivery: {
      available: !!doc.deliveryInstructions,
      radius: null,
      fee: null,
    },
    instructions: doc.deliveryInstructions || '',
    activated: !!doc.activated,
    // Session 6c1 — needed by isBusinessActive() derivation so customer-side
    // UI can gate order/contact features on the paid activation window.
    activationValidTill: doc.activationValidTill,
    // Item #12 — Customer-facing contact numbers. Mirror the same
    // fallback chain ManageBusinessTab uses when displaying these to
    // the merchant: explicit customerPhone / customerWhatsapp override
    // wins; otherwise we fall back to the registration phone. Empty
    // string remains possible if a business was created via admin
    // script and has no phone at all (defensive — Call/WhatsApp icons
    // gate themselves out in that case).
    customerPhone: (doc.customerPhone && doc.customerPhone.trim())
      || doc.phone || '',
    customerWhatsapp: (doc.customerWhatsapp && doc.customerWhatsapp.trim())
      || doc.phone || '',
    products: productsRaw.map(transformProduct),
    // Y11 — prototype About tab fields. tags is the merchant-curated
    // keyword list (max ~6 per category recommendation), instagramUrl
    // and websiteUrl are the social/web links (either may be empty),
    // bizCode is the human-readable business identifier (doc.id is
    // already in the slug form XXX-XXXX). All four are optional —
    // the rendering branches on emptiness.
    tags: Array.isArray(doc.tags) ? doc.tags : [],
    instagramUrl: doc.instagramUrl || '',
    websiteUrl: doc.websiteUrl || '',
    bizCode: doc.id || '',
    // Item #8 — FSSAI compliance fields. The customer view shows the
    // number (read-only) and whether it's verified. Certificate URL is
    // intentionally NOT exposed in the customer transform: the cert
    // photo is private (only the merchant + Aangan review team see it,
    // per the registration UI's "Visible only to you and the Aangan
    // review team" copy). category is needed downstream to gate the
    // section behind the food-category check.
    category: doc.category || '',
    fssaiNumber: (doc.fssaiNumber || '').trim(),
    fssaiVerified: !!doc.fssaiVerified,
    // Posts not in Firestore yet — leave empty so the posts tab shows
    // a graceful empty state. Will be a separate subcollection later.
    posts: [],
  };
}

function transformProduct(doc) {
  return {
    id: doc.id,
    name: doc.name,
    price: typeof doc.price === 'number' ? doc.price : 0,
    unit: doc.unit || '',
    icon: doc.icon || '📦',
    iconBg: doc.iconBg || 'linear-gradient(135deg, #6E6F84 0%, #9596A6 100%)',
    inStock: doc.inStock !== false, // default true if missing
    // Phase 2.5 / C10c — Customer-side was rendering only the emoji
    // fallback because this field wasn't being mapped. Merchant catalog
    // saves photos to `imageUrl`; mirroring that field here lets the
    // customer-side product card show the real photo too.
    imageUrl: doc.imageUrl || null,
  };
}


// ============================================================
// COMPONENT
// ============================================================

export default function BusinessProfile({ businessId, onBack, onExitV2, onOpenCart, onManageBusiness, onOpenPost }) {
  // ============ DATA LAYER (Step 3.3) ============
  // Profile + products loaded together from Firestore on mount/businessId change.
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    if (!businessId) {
      setError('No business selected');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      // Fetch business doc and products subcollection in parallel.
      // Promise.allSettled (not Promise.all) so a products-read failure
      // doesn't kill the whole profile load. This matters for pending
      // businesses: the products rule requires isVerifiedBusiness, so
      // non-owner customers get permission-denied on a pending biz's
      // products — which is by design (products shouldn't be customer-
      // visible until admin verifies). We still show the business
      // itself; the UI renders 'No products listed yet'.
      const [bizResult, productsResult] = await Promise.allSettled([
        getBusiness(businessId),
        getProducts(businessId),
      ]);
      if (bizResult.status === 'rejected') {
        // Biz doc itself is required — if this read fails, surface the error.
        throw bizResult.reason;
      }
      const bizDoc = bizResult.value;
      let productsRaw = [];
      if (productsResult.status === 'fulfilled') {
        productsRaw = productsResult.value;
      } else {
        // Most common cause: pending-verification biz viewed by non-owner.
        // Log at info-level (not error) so it doesn't pollute Sentry/etc.
        console.info(
          '[BusinessProfile] products not loaded (likely pending verification):',
          productsResult.reason?.code || productsResult.reason
        );
      }
      if (!bizDoc) {
        setError('Business not found');
        setProfile(null);
        return;
      }
      setProfile(transformBusinessProfile(bizDoc, productsRaw));
    } catch (err) {
      console.error('[BusinessProfile] load failed:', err);
      setError(err?.message || 'Failed to load business');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // sessionStorage key for the active sub-tab — survives navigation to
  // detail views (e.g. WallPostDetail) so Back returns the user to the
  // sub-tab they came from (Wall, not the default Products). Scoped per
  // business id so different businesses don't share state.
  const subTabStorageKey = businessId ? `bizProfile.subTab.${businessId}` : null;
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const stored = subTabStorageKey ? sessionStorage.getItem(subTabStorageKey) : null;
      return stored || 'products';
    } catch { return 'products'; }
  });
  // Sync subsequent tab changes to sessionStorage. Read on next mount.
  useEffect(() => {
    if (!subTabStorageKey) return;
    try { sessionStorage.setItem(subTabStorageKey, activeTab); } catch {}
  }, [activeTab, subTabStorageKey]);

  // Phase 2.5 / C10c — Wall posts (real-time). `null` = loading, [] = empty.
  const [wallPosts, setWallPosts] = useState(null);
  const [wallPhotoViewerUrl, setWallPhotoViewerUrl] = useState(null);
  // SharePostSheet state — null = closed, post object = open with that post
  const [sharingPost, setSharingPost] = useState(null);
  // Phase 2.5 / C10d — Set of post IDs the viewer has sparked. Refreshed
  // whenever the wall list changes. Optimistically updated on local spark
  // toggle so the icon flips instantly; reconciled via the real-time
  // listener for the sparkCount counter.
  const [sparkedSet, setSparkedSet] = useState(() => new Set());
  // ============ CONNECTION STATE (Step 3.4b) ============
  // Loaded from Firestore on mount/businessId change. Starts false (not
  // connected) until the load resolves. Connect/disconnect handlers below
  // call connectToBusiness/disconnectFromBusiness which write to Firestore
  // and we mirror the result here.
  const [connected, setConnected] = useState(false);
  const [showConnectSheet, setShowConnectSheet] = useState(false);
  const [showDisconnectSheet, setShowDisconnectSheet] = useState(false);
  // Item #13 Phase A Step 6 Part B — does this customer have an active
  // "notify when open" subscription on this biz? Drives the primary
  // button's label (and tap behaviour) when the biz is closed.
  // Resets to false on signOut + on biz switch; (re)loaded by the
  // same effect that loads `connected` below.
  const [openSubscribed, setOpenSubscribed] = useState(false);
  const [toast, setToast] = useState(null);

  // ============ CART STATE (Step 6.0d.1) ============
  // cartCount drives the badge on the primary CTA. justAddedId flashes
  // a "✓ Added" state on the most recently-added product card for ~900ms
  // then reverts to the normal "+ Add" pill. Both are local UI state —
  // the source of truth is localStorage via lib/v2/cart helpers.
  const [cartCount, setCartCount] = useState(() => getCartCount(businessId));
  const [justAddedId, setJustAddedId] = useState(null);

  // Re-read cart on businessId change (e.g. arriving here via deep link
  // from Connect List or HomeStripV2).
  useEffect(() => {
    setCartCount(getCartCount(businessId));
  }, [businessId]);

  // Load current connection status from Firestore on mount / businessId change.
  //
  // Y6 fix (Jun 16): subscribe to onAuthStateChanged instead of reading
  // auth.currentUser eagerly. Some navigation paths (e.g. MembersTabV2's
  // 'Owns' pill which uses window.location.search to navigate, triggering
  // a full page reload) cause this component to mount BEFORE Firebase Auth
  // finishes rehydrating from IndexedDB. In that race, getConnection sees
  // auth.currentUser === null, returns null, and we incorrectly set
  // connected=false even though an active connection exists.
  //
  // onAuthStateChanged fires once with the rehydrated user (or null) on
  // initial subscribe, and again on any sign-in/sign-out. Re-running
  // getConnection on each fire keeps the UI in sync.
  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (cancelled) return;
      if (!user) {
        // Signed out (or pre-rehydration with no session) — show unconnected.
        // If sign-in lands later, the listener fires again and we re-fetch.
        setConnected(false);
        setOpenSubscribed(false);
        return;
      }
      // Item #13 Phase A Step 6 Part B — load connection + open-alert
      // subscription in parallel. Both reads are independent and both
      // need to land before the primary CTA can render its final state.
      Promise.all([
        getConnection(businessId),
        isSubscribedToOpenAlert(businessId),
      ])
        .then(([conn, subscribed]) => {
          if (cancelled) return;
          // Only status='active' counts as "connected" in the UI
          setConnected(conn?.status === 'active');
          setOpenSubscribed(!!subscribed);
        })
        .catch(err => {
          // getConnection already catches permission-denied (no doc) and returns
          // null; only real errors land here. Don't surface — Connect button still
          // works, and connectToBusiness handles existing-doc reconnect cleanly.
          console.warn('[BusinessProfile] connection/subscription load failed:', err);
        });
    });
    return () => {
      cancelled = true;
      unsubAuth();
    };
  }, [businessId]);

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // ============ WALL POSTS SUBSCRIPTION (Phase 2.5 / C10c) ============
  // Subscribes only when the user actually opens the Wall sub-tab, to
  // avoid the realtime listener cost on visitors who never tap Wall.
  // Returns the unsub function so React cleans up on tab switch / unmount.
  useEffect(() => {
    if (activeTab !== 'wall' || !businessId) return;
    const unsub = listWallPosts({
      bizId: businessId,
      onSnap: (list) => setWallPosts(list),
      onError: (err) => {
        console.error('[BusinessProfile] wall posts subscribe error:', err);
        setWallPosts([]);
      },
      limit: 50,
    });
    return unsub;
  }, [activeTab, businessId]);

  // Phase 2.5 / C10d — Hydrate "sparkedByMe" set whenever the visible
  // wall list changes. Reads sparks/{userId} for each post in parallel.
  // Cost: 1 read per post per Wall tab visit (capped at the list limit).
  useEffect(() => {
    // Read uid inline (viewerUid is declared later in the function — it
    // would still resolve via closure but referencing it here triggers
    // the temporal dead zone error in strict mode).
    const uid = auth.currentUser?.uid;
    if (!uid || !businessId || !Array.isArray(wallPosts) || wallPosts.length === 0) {
      setSparkedSet(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      const ids = await getSparkedPostIds({
        bizId: businessId,
        userId: uid,
        postIds: wallPosts.map(p => p.id),
      });
      if (!cancelled) setSparkedSet(ids);
    })();
    return () => { cancelled = true; };
  }, [wallPosts, businessId]);

  // ============ LOADING / ERROR / NOT-FOUND EARLY RETURNS ============
  // All hooks above run on every render; conditional returns must come AFTER.
  if (loading) {
    return (
      <div className="v2-biz-profile" style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div>Loading business…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v2-biz-profile" style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <div style={{ marginBottom: 8 }}>Couldn't load business</div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>{error}</div>
        <button
          className="v2bd-cat-chip active"
          onClick={loadProfile}
          style={{ marginRight: 8 }}
        >
          Retry
        </button>
        <button className="v2bd-cat-chip" onClick={onBack}>
          Back
        </button>
      </div>
    );
  }

  if (!profile) {
    // Defensive — shouldn't normally hit this branch
    return (
      <div className="v2-biz-profile" style={{ padding: 40, textAlign: 'center' }}>
        <div>Business not found</div>
        <button className="v2bd-cat-chip" onClick={onBack} style={{ marginTop: 16 }}>
          Back
        </button>
      </div>
    );
  }

  async function handleConnectConfirm() {
    // Optimistic — flip state and close sheet immediately
    setConnected(true);
    setShowConnectSheet(false);
    try {
      await connectToBusiness(businessId);
      setToast(`Connected with ${profile.name}`);
    } catch (err) {
      console.error('[BusinessProfile] connect failed:', err);
      setConnected(false); // revert
      setToast('Failed to connect. Try again.');
    }
  }

  async function handleDisconnectConfirm() {
    // Optimistic — flip state and close sheet immediately
    setConnected(false);
    setShowDisconnectSheet(false);
    try {
      await disconnectFromBusiness(businessId);
      setToast(`Disconnected from ${profile.name}`);
    } catch (err) {
      console.error('[BusinessProfile] disconnect failed:', err);
      setConnected(true); // revert
      setToast('Failed to disconnect. Try again.');
    }
  }

  async function handlePrimaryAction() {
    if (connected) {
      // Session 6c1 — Notify Interested flow serves TWO cases:
      //   (a) Business is inactive (trial + activation both lapsed) —
      //       "Notify me when active"
      //   (b) Business is active but currently closed (out-of-hours) —
      //       "Notify when open"
      // Both write to the SAME /businesses/{bid}/openSubscribers/{uid}
      // collection via the existing subscribeToOpenAlert helper. When
      // the owner eventually reactivates AND opens for the day, the
      // Cloud Function notifyOpenSubscribers fires and pings both cohorts
      // together. Only the toast copy differs.
      //
      // The subscriber doc is one-shot: the notifyOpenSubscribers Cloud
      // Function deletes it after firing the notification, so a follow-up
      // open event won't re-notify unless the customer re-subscribes.
      if (!bizActive || !profile.open) {
        // Optimistic flip — UI feels instant. Revert on failure.
        const next = !openSubscribed;
        setOpenSubscribed(next);
        try {
          if (next) {
            // reason tells the CF layer which cohort this is: an inactive
            // business is woken by onActivationEvent on reactivation, a
            // closed-but-active one by notifyOpenSubscribers on open.
            await subscribeToOpenAlert(businessId, bizActive ? 'closed' : 'inactive');
            setToast(
              !bizActive
                ? `We'll notify you when ${profile.name} is active again`
                : `We'll notify you when ${profile.name} opens`
            );
          } else {
            await unsubscribeFromOpenAlert(businessId);
            setToast('Notification turned off');
          }
        } catch (err) {
          console.error('[BusinessProfile] alert toggle failed:', err);
          // Revert — the Firestore write didn't land.
          setOpenSubscribed(!next);
          setToast('Couldn\'t update — try again');
        }
        return;
      }
      // If connected and open → navigate to Cart
      if (onOpenCart) {
        onOpenCart(businessId);
      } else {
        setToast('Cart screen not available');
      }
    } else {
      setShowConnectSheet(true);
    }
  }

  // 6.0d.1: Add a product to cart from the Products list.
  // Quantity controls + remove live on the Cart screen — here we just
  // increment (or insert at qty=1) and flash a "✓ Added" state on the
  // tapped card for ~900ms.
  function handleAdd(product) {
    if (!product?.id) return;
    if (!product.inStock) {
      setToast('Out of stock');
      return;
    }
    const newCount = addToCart(businessId, product.id);
    setCartCount(newCount);
    setJustAddedId(product.id);
    setTimeout(() => {
      setJustAddedId(prev => prev === product.id ? null : prev);
    }, 900);
  }

  // Y10 (Path C): a business is "transactable" — customer can connect,
  // call, chat, and order — when its status is 'verified' OR
  // 'pending_verification'. Pending businesses are in their 15-day trial
  // and the server-side rule (isTransactableBusiness) already permits
  // their /connections, /orders, /products operations. We mirror that
  // gate client-side so the UI doesn't lie.
  //
  // Session 6c1 — extended with bizActive gate: even a verified biz whose
  // paid activation window has lapsed AND whose trial is over is no
  // longer transactable. Backend enforcement of this same gate lands in
  // Session 6b (firestore.rules extension).
  const bizActive = isBusinessActive(profile);
  const isTransactable = (profile.status === 'verified'
    || profile.status === 'pending_verification')
    && bizActive;

  // Phone/WhatsApp visibility: gated by three conditions —
  //   (a) user is connected
  //   (b) business is transactable (verified or in trial)
  //   (c) business is currently open (hours-derived OR manualOpenOverride)
  // Item #12 — Closed-business gating was added so customers don't
  // call/WhatsApp a closed shop expecting an immediate response. The
  // icon stays visible but greyed out; tap shows a toast explaining
  // why and (when applicable) when the business reopens.
  const contactGated = !(connected && isTransactable && profile.open);

  // Item #12 — Compute the right gating-reason toast given which of
  // the three conditions failed. The order matters: connection is the
  // hard prerequisite (customer needs to actually be connected before
  // we hand out phone numbers), transactability is a status check,
  // open-status is the most-likely-to-flip-soon state so we give the
  // most informative copy there.
  function gatedReason() {
    if (!connected) return "Tap '+ Connect' to call or message";
    if (!isTransactable) return 'This business isn\'t available for contact right now';
    // Connected + transactable but closed.
    if (profile.manuallyPaused) {
      return 'Currently closed';
    }
    // Out of hours — openTimeStr is the next-open string like
    // "Opens at 11 AM". When available, include it so the customer
    // knows when to come back.
    return profile.openTimeStr
      ? `Currently closed · ${profile.openTimeStr}`
      : 'Currently closed';
  }

  // Item #12 — Real tel: handler. Normalises the stored number by
  // stripping spaces / dashes / parentheses (so a number stored as
  // "+91 90000 00000" still produces a valid tel URI). The tel scheme
  // is the universal way to trigger a dialler across Android, iOS,
  // and desktop OSes that have a registered handler; on machines
  // without a dialler it's a no-op, which is acceptable.
  function handleCall() {
    if (contactGated) {
      setToast(gatedReason());
      return;
    }
    const raw = profile.customerPhone || '';
    const cleaned = raw.replace(/[\s\-()]/g, '');
    if (!cleaned) {
      setToast('No phone number on file');
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.href = `tel:${cleaned}`;
    }
  }

  // Item #12 — Real wa.me handler. WhatsApp's click-to-chat URL needs
  // a digits-only international number (no +, spaces, dashes or
  // parentheses). Empty cleaned → bail with toast. Open in a new tab
  // so the chat doesn't replace the user's biz-profile session.
  function handleWhatsApp() {
    if (contactGated) {
      setToast(gatedReason());
      return;
    }
    const raw = profile.customerWhatsapp || '';
    const cleaned = raw.replace(/[^\d]/g, ''); // wa.me needs digits only
    if (!cleaned) {
      setToast('No WhatsApp number on file');
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(`https://wa.me/${cleaned}`, '_blank', 'noopener,noreferrer');
    }
  }

  // I5: real share handler. Builds a deep link to this profile and
  // tries navigator.share (mobile/PWA-friendly); falls back to copying
  // the URL to the clipboard if Web Share API is unavailable (most
  // desktop browsers) or if the user cancels the native sheet. The
  // url-based deep link relies on Y7's URL-sync so any recipient
  // tapping the link lands on the right profile.
  async function handleShare() {
    if (typeof window === 'undefined') return;
    // P1: must be the PUBLIC origin, not window.location.origin — on
    // Capacitor native that is http://localhost and the recipient gets a
    // dead link. Path shape left as-is; App.jsx parses this legacy
    // ?v2=biz-profile&id=X form as well as /biz/ID.
    const shareUrl = buildShareUrl(`/?v2=biz-profile&id=${businessId}`);
    const shareData = {
      title: profile.name,
      text: `Check out ${profile.name} on Aangan`,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        // Native sheet handled it; no toast needed
        return;
      } catch (err) {
        // User cancelled (AbortError) — silently do nothing. Other
        // errors (e.g. permission denied) fall through to clipboard.
        if (err && err.name === 'AbortError') return;
      }
    }
    // Fallback: clipboard copy
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setToast('Link copied to clipboard');
      } else {
        // Older browsers: show the URL so user can copy manually
        setToast(`Share link: ${shareUrl}`);
      }
    } catch (err) {
      console.error('[BusinessProfile] share failed:', err);
      setToast('Could not share');
    }
  }

  // What does the primary action button say?
  let primaryLabel = '+ Connect';
  let primaryDisabled = false;
  if (connected) {
    if (!bizActive) {
      // Session 6c1 — trial-expired-and-not-activated. Customer can
      // subscribe to be pinged when the owner reactivates + opens next.
      // Same openSubscribers plumbing as "notify when open".
      primaryLabel = openSubscribed
        ? '✓ Notification set'
        : '🔔 Notify me when active';
    } else if (!profile.open) {
      // Item #13 Phase A Step 6 Part B — label differentiates whether
      // the customer has already subscribed to "notify when open".
      // Tapping the button toggles state (see handlePrimaryAction).
      primaryLabel = openSubscribed
        ? '✓ Notification set'
        : '🔔 Notify when open';
    } else if (cartCount > 0) {
      primaryLabel = `View cart · ${cartCount}`;
    } else {
      primaryLabel = 'Order';
    }
  }

  // Y1: viewer-is-owner detection. Owners shouldn't see customer CTAs
  // (+ Connect / Call / Chat / Order) for their own business — they get
  // a 'Manage this business' shortcut to MerchantApp instead.
  // Falls back to false during the brief load window before auth or
  // profile is hydrated, so non-owners never see the owner CTA flash.
  const viewerUid = auth.currentUser?.uid;
  const isOwner = !!(viewerUid && profile.ownerId && viewerUid === profile.ownerId);

  return (
    <div className="v2-biz-profile">
      {/* ============ HERO ============ */}
      {/* C10c — Hero uses the uploaded cover photo if present, otherwise
          falls back to the category gradient. Same pattern the merchant
          top bar uses. */}
      <div className="v2bp-hero"
           style={{
             background: profile.coverUrl
               ? `url("${profile.coverUrl}") center/cover no-repeat`
               : profile.coverBg,
           }}>
        {/* Floating top bar */}
        <div className="v2bp-overlay-top-bar">
          <button className="v2bp-overlay-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="v2bp-overlay-btn"
            onClick={handleShare}
            aria-label="Share"
          >
            <Share2 size={18} />
          </button>
          {connected && (
            <button
              className="v2bp-overlay-btn"
              onClick={() => setShowDisconnectSheet(true)}
              aria-label="More options"
            >
              <MoreVertical size={18} />
            </button>
          )}
        </div>

        {/* Logo on hero edge — uses uploaded photo if present */}
        <div
          className="v2bp-hero-logo"
          style={{
            background: profile.logoImageUrl ? 'transparent' : profile.logoBg,
            overflow: 'hidden',
          }}
        >
          {profile.logoImageUrl ? (
            <img
              src={profile.logoImageUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            profile.logo
          )}
        </div>
      </div>

      {/* ============ MAIN CONTENT ============ */}
      <div className="v2bp-content">
        {/* Identity block */}
        <div className="v2bp-identity">
          <div className="v2bp-identity-name-row">
            <h1 className="v2bp-identity-name">{profile.name}</h1>
            {/* Session 6c1.5 polish — verified checkmark gated on
                bizActive so it doesn't show alongside the INACTIVE pill.
                Matches the precedence rule used in BusinessDirectory and
                ConnectList: when a biz is inactive, the inactive signal
                wins over all other identity chrome (verified check +
                pending badge both suppressed). */}
            {profile.status === 'verified' && bizActive && (
              <span className="v2bp-verified-md">
                <Check size={11} strokeWidth={3} />
              </span>
            )}
            {/* Session 6c1 — Inactive pill takes precedence over Pending.
                Rationale: a customer looking at an inactive-and-pending biz
                needs to know the strongest actionable signal (they can't
                transact), not the softer "we're still checking this out". */}
            {!bizActive ? (
              <span className="v2bp-inactive-pill">Inactive</span>
            ) : profile.status === 'pending_verification' && (
              <span className="v2bp-pending-pill">Pending</span>
            )}
          </div>
          <p className="v2bp-identity-tagline">{profile.tagline}</p>
          <p className="v2bp-identity-meta">
            <span>{profile.categoryLabel}</span>
            <span>·</span>
            <span className="v2bp-meta-pin">
              <MapPin size={10} strokeWidth={2.2} />
              <span>{profile.locality}, {profile.city}</span>
            </span>
          </p>
        </div>

        {/* Session 6c1 — Inactive banner takes precedence over the
            pending-verification banner. Shown when the business's paid
            activation window has lapsed AND the trial window is over.
            Customer can still Connect + browse; Order + call + chat are
            gated (see isTransactable). */}
        {!bizActive && (
          <div className="v2bp-banner v2bp-banner-inactive">
            <span>⏸</span>
            <span>
              <b>This business is temporarily inactive.</b>{' '}
              You can be notified when they're back.
            </span>
          </div>
        )}

        {/* Pending verification banner — Y10: explicit status check
            (not !verified) so suspended businesses don't get a banner
            saying they're being verified.
            Session 6c1 — suppressed when the inactive banner is showing,
            since inactive-and-pending is confusing to a customer. */}
        {bizActive && profile.status === 'pending_verification' && (
          <div className="v2bp-banner v2bp-banner-pending">
            <span>ⓘ</span>
            <span>This business is being verified by the Aangan team</span>
          </div>
        )}

        {/* Closed banner — Y10: shows for any transactable business that's
            currently closed (verified OR pending). Pending biz hours are
            real working hours just like verified biz. */}
        {isTransactable && !profile.open && (
          <div className="v2bp-banner v2bp-banner-closed">
            <span>🌙</span>
            <span><b>Closed right now</b> · {profile.openTimeStr}</span>
          </div>
        )}

        {/* Action row — Y1: owner sees 'Manage this business'; customer sees Connect/Call/Chat */}
        <div className="v2bp-actions-row">
          {isOwner ? (
            <button
              className="v2bp-action-primary"
              onClick={() => {
                if (onManageBusiness) onManageBusiness(businessId);
                else setToast('Open Merchant tab to manage this business');
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Settings size={16} />
              Manage this business
            </button>
          ) : (
            <>
              <button
                className={`v2bp-action-primary ${connected ? 'connected' : ''}`}
                onClick={handlePrimaryAction}
                disabled={primaryDisabled}
              >
                {primaryLabel}
              </button>
              <button
                className={`v2bp-action-icon ${contactGated ? 'gated' : ''}`}
                onClick={handleCall}
                title="Call"
                aria-label="Call"
              >
                <Phone size={18} />
              </button>
              <button
                className={`v2bp-action-icon ${contactGated ? 'gated' : ''}`}
                onClick={handleWhatsApp}
                title="WhatsApp"
                aria-label="WhatsApp"
              >
                <MessageCircle size={18} />
              </button>
            </>
          )}
        </div>

        {/* Connect helper text for unconnected transactable businesses
            (non-owner only). Y10: pending biz qualifies too (Path C). */}
        {!isOwner && !connected && isTransactable && (
          <p className="v2bp-contact-helper">
            Connect with this business to see their phone & WhatsApp
          </p>
        )}

        {/* Info strip */}
        <div className="v2bp-info-strip">
          <div className="v2bp-info-line">
            <div className={`v2bp-info-icon ${profile.open ? 'open' : 'closed'}`}>
              <Clock size={14} />
            </div>
            <div className="v2bp-info-text">
              <b>{profile.openTimeStr}</b>
              {profile.todayHoursStr !== '—' && ` · Today: ${profile.todayHoursStr}`}
            </div>
          </div>
          {profile.delivery.available && (
            <div className="v2bp-info-line">
              <div className="v2bp-info-icon delivery">
                <Truck size={14} />
              </div>
              <div className="v2bp-info-text">
                <b>Delivery available</b> · Within {profile.delivery.radius} · {profile.delivery.fee}
              </div>
            </div>
          )}
          {profile.instructions && (
            <div className="v2bp-info-line">
              <div className="v2bp-info-icon notes">
                <FileText size={14} />
              </div>
              <div className="v2bp-info-text">{profile.instructions}</div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="v2bp-tabs-bar">
          <button
            className={`v2bp-tab ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products{profile.products.length > 0 ? ` (${profile.products.length})` : ''}
          </button>
          <button
            className={`v2bp-tab ${activeTab === 'wall' ? 'active' : ''}`}
            onClick={() => setActiveTab('wall')}
          >
            Wall
          </button>
          <button
            className={`v2bp-tab ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
        </div>

        {/* Tab content */}
        <div className="v2bp-tab-content">
          {activeTab === 'products' && (
            // Y11: vertical list (was 2-col grid). Inline-styled to
            // override v2bp-products-grid's grid layout without touching
            // the stylesheet. Each row matches prototype:
            //   [56px thumb] [name + desc + price] [+ Add pill]
            <div className="v2bp-products-grid" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
              {profile.products.length === 0 ? (
                <div className="v2bp-empty">
                  <div className="v2bp-empty-icon">📦</div>
                  <div className="v2bp-empty-title">No products listed yet</div>
                </div>
              ) : (
                profile.products.map(p => {
                  const isAdded = justAddedId === p.id;
                  const disabled = !p.inStock;
                  const canAdd = connected && profile.open && p.inStock;
                  return (
                    <div
                      key={p.id}
                      className={`v2bp-product-card ${!p.inStock ? 'out-of-stock' : ''}`}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      {/* Thumb — 56px square, was full-width in grid layout.
                          C10c — Shows product photo if uploaded; falls back
                          to the emoji on the category gradient otherwise. */}
                      <div
                        className="v2bp-product-icon"
                        style={{
                          width: 56,
                          height: 56,
                          aspectRatio: '1',
                          margin: 0,
                          fontSize: 26,
                          flexShrink: 0,
                          background: p.imageUrl ? 'transparent' : p.iconBg,
                          overflow: 'hidden',
                        }}
                      >
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt=""
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          p.icon
                        )}
                      </div>

                      {/* Body — name, optional desc, price */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          className="v2bp-product-name"
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            marginBottom: 2,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.name}
                        </div>
                        {p.description && (
                          <div style={{
                            fontSize: 11,
                            color: 'var(--ink-soft)',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                          }}>
                            {p.description}
                          </div>
                        )}
                        <div style={{ marginTop: 4, fontSize: 13 }}>
                          <b style={{
                            fontFamily: "'Fraunces', Georgia, serif",
                            fontWeight: 600,
                            color: 'var(--accent, #C45A3F)',
                          }}>
                            ₹{p.price}
                          </b>
                          {p.unit && (
                            <span style={{
                              fontSize: 11,
                              color: 'var(--ink-soft)',
                              marginLeft: 4,
                            }}>{p.unit}</span>
                          )}
                        </div>
                        {!p.inStock && (
                          <div style={{
                            marginTop: 4,
                            fontSize: 11,
                            color: 'var(--danger, #B33A3A)',
                            fontWeight: 500,
                          }}>
                            Out of stock
                          </div>
                        )}
                      </div>

                      {/* +Add pill — right side */}
                      {canAdd && (
                        <button
                          onClick={() => handleAdd(p)}
                          disabled={disabled || isAdded}
                          style={{
                            flexShrink: 0,
                            padding: '6px 14px',
                            fontSize: 12,
                            fontWeight: 500,
                            border: '1px solid var(--accent, #C45A3F)',
                            borderRadius: 18,
                            cursor: disabled ? 'default' : 'pointer',
                            background: isAdded
                              ? 'var(--accent, #C45A3F)'
                              : 'var(--accent-soft, #FBE8D6)',
                            color: isAdded
                              ? '#fff'
                              : 'var(--accent, #C45A3F)',
                            fontFamily: 'inherit',
                            transition: 'all 120ms ease',
                            WebkitTapHighlightColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isAdded ? (
                            <>
                              <Check size={13} strokeWidth={3} />
                              <span>Added</span>
                            </>
                          ) : (
                            <>
                              <Plus size={13} strokeWidth={2.5} />
                              <span>Add</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'wall' && (
            // Phase 2.5 / C10c — Real wall posts. Subscription lives in
            // the useEffect above (only active while this tab is open).
            //
            // Engagement wiring:
            //   Spark   → toggleSpark inline (no navigation; counter
            //             refreshes via the real-time subscription).
            //   Comment → open post detail screen where the comment
            //             thread + composer live.
            //   Share   → open SharePostSheet inline (chat picker /
            //             external share / copy link).
            //   Photo   → open ImageViewerModal (zoom).
            //   Card body (text/badge area) → open post detail.
            <div className="v2bp-wall-list">
              {wallPosts === null ? (
                <div className="v2bp-empty">
                  <div className="v2bp-empty-icon">⏳</div>
                  <div className="v2bp-empty-title">Loading…</div>
                </div>
              ) : wallPosts.length === 0 ? (
                <div className="v2bp-empty">
                  <div className="v2bp-empty-icon">📝</div>
                  <div className="v2bp-empty-title">No posts yet</div>
                  <div className="v2bp-empty-sub">{profile.name} hasn't shared anything yet.</div>
                </div>
              ) : (
                wallPosts.map(post => (
                  <div key={post.id}
                       onClick={() => onOpenPost?.(businessId, post.id)}
                       style={{ cursor: 'pointer' }}>
                    <WallPostCard
                      post={post}
                      variant="customer"
                      sparkedByMe={sparkedSet.has(post.id)}
                      onTapPhoto={(url) => setWallPhotoViewerUrl(url)}
                      onSpark={async () => {
                        if (!viewerUid) {
                          setToast('Sign in to spark posts');
                          return;
                        }
                        // Optimistic UI: flip the local set before the
                        // server round-trip so the icon updates instantly.
                        // Counter updates via the real-time wallPosts listener.
                        const wasSparked = sparkedSet.has(post.id);
                        setSparkedSet(prev => {
                          const next = new Set(prev);
                          if (wasSparked) next.delete(post.id);
                          else next.add(post.id);
                          return next;
                        });
                        try {
                          await toggleSpark({ bizId: businessId, postId: post.id, userId: viewerUid });
                        } catch (err) {
                          console.error('toggleSpark failed:', err);
                          // Revert on failure
                          setSparkedSet(prev => {
                            const next = new Set(prev);
                            if (wasSparked) next.add(post.id);
                            else next.delete(post.id);
                            return next;
                          });
                          setToast(`Couldn't spark: ${err.code || err.message || 'try again'}`);
                        }
                      }}
                      onComment={() => onOpenPost?.(businessId, post.id)}
                      onShare={() => setSharingPost(post)}
                      // Session C1 — report a wall post. Shown only when the
                      // viewer is signed in AND does not own this business
                      // (you can't report your own post). Writes a
                      // business_post report to /reports for the super-admin.
                      onReport={(viewerUid && !isOwner) ? (async ({ reason, detail }) => {
                        try {
                          const res = await submitBusinessReport({
                            businessId,
                            targetId: post.id,
                            reporterId: viewerUid,
                            reason,
                            detail,
                          });
                          setToast(res.duplicate
                            ? 'Already reported — we\'re reviewing.'
                            : 'Reported. We\'ll review within 24 hours.');
                        } catch (err) {
                          console.error('submitBusinessReport failed:', err);
                          setToast('Could not submit report — try again');
                        }
                      }) : null}
                    />
                  </div>
                ))
              )}
              {wallPhotoViewerUrl && (
                <ImageViewerModal
                  src={wallPhotoViewerUrl}
                  onClose={() => setWallPhotoViewerUrl(null)}
                />
              )}
              {sharingPost && (
                <SharePostSheet
                  post={sharingPost}
                  currentUser={{ id: viewerUid }}
                  onClose={() => setSharingPost(null)}
                  onSetToast={(msg) => setToast(msg)}
                />
              )}
            </div>
          )}

          {activeTab === 'about' && (
            <div className="v2bp-about">
              {/* About / Description — kept; not in prototype but useful */}
              {profile.description && (
                <section className="v2bp-about-section">
                  <h3 className="v2bp-about-h">About</h3>
                  <p className="v2bp-about-text">{profile.description}</p>
                </section>
              )}

              {/* Hours */}
              {profile.hours.length > 0 && (
                <section className="v2bp-about-section">
                  <h3 className="v2bp-about-h">Hours</h3>
                  <table className="v2bp-hours-table">
                    <tbody>
                      {profile.hours.map((h, i) => (
                        <tr key={i}>
                          <td>{h.day}</td>
                          <td>{h.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {/* Address — renamed from "Location" to match prototype */}
              <section className="v2bp-about-section">
                <h3 className="v2bp-about-h">Address</h3>
                <p className="v2bp-about-text">
                  <MapPin size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                  {profile.address}
                </p>
              </section>

              {/* Y11 — Tags (pill row). Skipped if merchant didn't curate any. */}
              {profile.tags && profile.tags.length > 0 && (
                <section className="v2bp-about-section">
                  <h3 className="v2bp-about-h">Tags</h3>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                  }}>
                    {profile.tags.map((tag, i) => (
                      <span
                        key={i}
                        style={{
                          background: 'var(--bg, #F7F0E5)',
                          border: '1px solid var(--border, #E8DDC6)',
                          color: 'var(--ink, #2A2418)',
                          fontSize: 11,
                          fontWeight: 500,
                          padding: '4px 10px',
                          borderRadius: 999,
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Y11 — Links (Instagram + website). Only renders if at
                  least one link is present; each link only renders if
                  the corresponding URL field is non-empty. */}
              {(profile.instagramUrl || profile.websiteUrl) && (
                <section className="v2bp-about-section">
                  <h3 className="v2bp-about-h">Links</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {profile.instagramUrl && (
                      <a
                        href={profile.instagramUrl.startsWith('http')
                          ? profile.instagramUrl
                          : `https://${profile.instagramUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '10px 12px',
                          background: 'var(--bg, #F7F0E5)',
                          border: '1px solid var(--border, #E8DDC6)',
                          borderRadius: 10,
                          textDecoration: 'none',
                          color: 'var(--ink, #2A2418)',
                          fontSize: 13,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>📷</span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {profile.instagramUrl.replace(/^https?:\/\//, '')}
                        </span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 17 17 7"/><path d="M7 7h10v10"/>
                        </svg>
                      </a>
                    )}
                    {profile.websiteUrl && (
                      <a
                        href={profile.websiteUrl.startsWith('http')
                          ? profile.websiteUrl
                          : `https://${profile.websiteUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '10px 12px',
                          background: 'var(--bg, #F7F0E5)',
                          border: '1px solid var(--border, #E8DDC6)',
                          borderRadius: 10,
                          textDecoration: 'none',
                          color: 'var(--ink, #2A2418)',
                          fontSize: 13,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>🌐</span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {profile.websiteUrl.replace(/^https?:\/\//, '')}
                        </span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 17 17 7"/><path d="M7 7h10v10"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </section>
              )}

              {/* ============ FSSAI LICENSE (food categories only) ============ */}
              {/* Item #8 — Read-only display of the merchant's FSSAI
                  number. Only renders for food categories AND only when
                  the merchant has actually entered a number — empty
                  state would clutter the panel for businesses that
                  haven't filled it in yet (or are still registering).
                  Certificate photo is intentionally NOT shown: per the
                  registration UI's contract, the cert is visible only
                  to the merchant + Aangan review team. */}
              {FOOD_CATEGORIES.has(profile.category) && profile.fssaiNumber && (
                <section className="v2bp-about-section">
                  <h3 className="v2bp-about-h">FSSAI License</h3>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
                    fontSize: 13,
                    color: 'var(--ink, #2A2418)',
                    padding: '8px 12px',
                    background: 'var(--bg, #F7F0E5)',
                    border: '1px solid var(--border, #E8DDC6)',
                    borderRadius: 8,
                    letterSpacing: '0.05em',
                  }}>
                    <span style={{ flex: 1 }}>{profile.fssaiNumber}</span>
                    {profile.fssaiVerified && (
                      <span
                        title="FSSAI verified by Aangan"
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--success, #2E7D32)',
                          background: 'rgba(46, 125, 50, 0.12)',
                          padding: '2px 7px',
                          borderRadius: 999,
                          letterSpacing: 0,
                          fontFamily: 'inherit',
                        }}
                      >
                        Verified ✓
                      </span>
                    )}
                  </div>
                </section>
              )}

              {/* Y11 — Business ID (human-readable slug). Always shown
                  for transparency and dispute reference. */}
              {profile.bizCode && (
                <section className="v2bp-about-section">
                  <h3 className="v2bp-about-h">Business ID</h3>
                  <div style={{
                    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
                    fontSize: 13,
                    color: 'var(--ink, #2A2418)',
                    padding: '8px 12px',
                    background: 'var(--bg, #F7F0E5)',
                    border: '1px solid var(--border, #E8DDC6)',
                    borderRadius: 8,
                    textAlign: 'center',
                    letterSpacing: '0.05em',
                  }}>
                    {profile.bizCode}
                  </div>
                </section>
              )}

              {/* Y11 — Aangan role disclaimer (prototype copy).
                  Replaces the older non-payment-only blurb. */}
              <section className="v2bp-about-section v2bp-disclaimer">
                <p style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: 0 }}>
                  <span style={{ flexShrink: 0, marginTop: 2 }}>ⓘ</span>
                  <span>
                    <b style={{ display: 'block', marginBottom: 4 }}>About Aangan's role</b>
                    Aangan connects you with this business. The business handles
                    all orders, deliveries, payments, refunds, and customer
                    service. For order issues, contact the business directly.
                    For platform issues, email{' '}
                    <a
                      href="mailto:aangansupport@gmail.com"
                      style={{ color: 'var(--info, #3F6BC4)', textDecoration: 'underline' }}
                    >
                      aangansupport@gmail.com
                    </a>.
                  </span>
                </p>
              </section>
            </div>
          )}
        </div>
      </div>

      {/* ============ CONNECT CONFIRMATION SHEET ============ */}
      {showConnectSheet && (
        <div className="v2bp-sheet-backdrop" onClick={() => setShowConnectSheet(false)}>
          <div className="v2bp-sheet" onClick={e => e.stopPropagation()}>
            <div className="v2bp-sheet-handle" />
            <div className="v2bp-sheet-title">Connect with {profile.name}?</div>

            <div className="v2bp-sheet-rows">
              <div className="v2bp-sheet-row">
                <Check size={16} strokeWidth={2.5} style={{ color: 'var(--success)' }} />
                <span>Their posts appear in your Connect List</span>
              </div>
              <div className="v2bp-sheet-row">
                <Check size={16} strokeWidth={2.5} style={{ color: 'var(--success)' }} />
                <span>Place orders when they're open</span>
              </div>
              <div className="v2bp-sheet-row">
                <span style={{ color: 'var(--ink-soft)' }}>ⓘ</span>
                <span>Phone/WhatsApp visible after they activate Aangan Connect</span>
              </div>
              <div className="v2bp-sheet-row">
                <span style={{ color: 'var(--ink-soft)' }}>↺</span>
                <span>You can disconnect anytime</span>
              </div>
            </div>

            <div className="v2bp-sheet-actions">
              <button
                className="v2bp-sheet-btn v2bp-sheet-btn-primary"
                onClick={handleConnectConfirm}
              >
                Connect
              </button>
              <button
                className="v2bp-sheet-btn v2bp-sheet-btn-ghost"
                onClick={() => setShowConnectSheet(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ DISCONNECT CONFIRMATION SHEET ============ */}
      {showDisconnectSheet && (
        <div className="v2bp-sheet-backdrop" onClick={() => setShowDisconnectSheet(false)}>
          <div className="v2bp-sheet" onClick={e => e.stopPropagation()}>
            <div className="v2bp-sheet-handle" />
            <div className="v2bp-sheet-title">Disconnect from {profile.name}?</div>

            <div className="v2bp-sheet-rows">
              <div className="v2bp-sheet-row">
                <span style={{ color: 'var(--danger)' }}>✗</span>
                <span>Their posts will no longer appear in your Connect List</span>
              </div>
              <div className="v2bp-sheet-row">
                <span style={{ color: 'var(--danger)' }}>✗</span>
                <span>You won't be able to place new orders</span>
              </div>
              <div className="v2bp-sheet-row">
                <span style={{ color: 'var(--ink-soft)' }}>ⓘ</span>
                <span>Your past orders and chats stay safe</span>
              </div>
              <div className="v2bp-sheet-row">
                <span style={{ color: 'var(--ink-soft)' }}>↺</span>
                <span>You can reconnect anytime</span>
              </div>
            </div>

            <div className="v2bp-sheet-actions">
              <button
                className="v2bp-sheet-btn v2bp-sheet-btn-danger"
                onClick={handleDisconnectConfirm}
              >
                Disconnect
              </button>
              <button
                className="v2bp-sheet-btn v2bp-sheet-btn-ghost"
                onClick={() => setShowDisconnectSheet(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ TOAST ============ */}
      {toast && (
        <div className="v2bp-toast">{toast}</div>
      )}
    </div>
  );
}
